import React from "react";
import { useDrag } from "react-dnd";
import { ItemTypes } from "../team-page/itemTypes.js";
import { findObjectByKeyValue, numToPrice } from "../utils.js";
import { Link } from "react-router-dom";
import { Popup } from "../common/Popup.jsx";
import { toast } from 'react-toastify';
import { computeNextMinimumBid } from '../../utils/bidValidation.js';
import { useQueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';

function TransferFlowPopup({
  info,
  popEvent,
  setPopEvent,
  popOpen,
  closePopup,
  handleSubmit,
  handleBoost,
  handleDrop, // Added handleDrop prop
  selectedPlayer,
  submitting,
  submitError,
  isQuickplayLeague = false,
}) {
  const queryClient = useQueryClient();
  const boostOptions = [
    { value: "2", label: "+5m € - 2$" },
    { value: "3", label: "10m € - 3$" },
    { value: "4", label: "20m € - 4$" },
  ];

  const [selectedOption, setSelectedOption] = React.useState("");

  const handleChange = (event) => {
    setSelectedOption(event.target.value);
  };

  let popupContent;

  const BoostOptions = boostOptions.map((item, index) => {
    return (
      <div className="checkbox-block form-group round" key={index}>
        <input
          type="radio"
          id={`user_option_${index}`} // ensure unique IDs
          name="user_options" // all radio buttons in the same group
          value={item.value} // use item value for the radio option
          checked={selectedOption === item.value} // controlled input
          onChange={handleChange} // handle change event
        />
        <label htmlFor={`user_option_${index}`}>
          {item.label} {/* Assuming item has a label property */}
        </label>
      </div>
    );
  });

  // State for bid form
  const [bidAmount, setBidAmount] = React.useState("");
  const [bidError, setBidError] = React.useState(null);

  // Derive currentPrice from selectedPlayer, fallback 0
  const rawCurrentPrice = selectedPlayer?.currentPrice;
  const currentPrice = Number.isFinite(rawCurrentPrice) ? rawCurrentPrice : 0;
  const isFreeAgent = selectedPlayer?.isFreeAgent === true;
  const inUpdateFlow = Number.isFinite(selectedPlayer?.myOffer);
  const marketValue = selectedPlayer?.value ?? selectedPlayer?.marketValue ?? 0;
  const serverSuggestedMin = Number.isFinite(selectedPlayer?.nextMinimumBid)
    ? selectedPlayer.nextMinimumBid
    : null;

  let minBid = computeNextMinimumBid({
    isFreeAgent,
    marketValue,
    currentPrice
  });

  if (serverSuggestedMin != null) {
    minBid = Math.max(minBid, serverSuggestedMin);
  }

  if (inUpdateFlow) {
    if (isFreeAgent) {
      const base = Math.max(currentPrice, selectedPlayer?.myOffer ?? 0);
      const updateFloor = base + 1;
      const computed = computeNextMinimumBid({
        isFreeAgent,
        marketValue,
        currentPrice
      });
      minBid = Math.max(updateFloor, computed, serverSuggestedMin ?? 0);
    } else {
      minBid = 1;
    }
  }

  // Quick Play locks the bid to the player's exact transfer value.
  const quickplayFixedBid = Number.isFinite(marketValue) ? marketValue : 0;
  if (isQuickplayLeague) {
    minBid = quickplayFixedBid;
  }
  const quickplayPendingBid = isQuickplayLeague && inUpdateFlow;

  // Block impossible bids before they reach the server. Raising your own
  // free-agent bid refunds the old reservation, so that amount counts as
  // available again. An unknown budget must not block bidding; the server
  // still validates every bid.
  const effectiveBidAmount = isQuickplayLeague ? quickplayFixedBid : Number(bidAmount || 0);
  const ownPendingOfferRefund =
    inUpdateFlow && isFreeAgent && Number.isFinite(selectedPlayer?.myOffer)
      ? selectedPlayer.myOffer
      : 0;
  const availableBudget = Number.isFinite(info?.remainingBudget)
    ? info.remainingBudget + ownPendingOfferRefund
    : null;
  const insufficientBudget =
    availableBudget !== null && effectiveBidAmount > availableBudget;

  const hasHighestBid = currentPrice > 0;

  // Reset form when popup opens with a new player
  React.useEffect(() => {
    if (selectedPlayer && popEvent === "bid") {
      if (isQuickplayLeague) {
        setBidAmount(String(quickplayFixedBid));
      } else {
        setBidAmount(minBid ? String(minBid) : "");
      }
      setBidError(null);
    }
  }, [selectedPlayer, popEvent, minBid, isQuickplayLeague, quickplayFixedBid]);

  switch (popEvent) {
    case "bid":
      popupContent = (
        <Popup popOpen={popOpen} close={closePopup}>
          <h3>SUBMIT BID</h3>

          {selectedPlayer && (
            <div className="player-info player-info--bid">
              <div className="player-info-top">
                <div className="player-name">{selectedPlayer.name}</div>
                <div className="player-ownership">
                  {isFreeAgent ? (
                    <span className="ownership-status free-agent">Free Agent</span>
                  ) : selectedPlayer.currentTeam?.name ? (
                    <>
                      <span className="ownership-prefix">Owned by</span>
                      <span className="ownership-status owned">
                        {selectedPlayer.currentTeam.name}
                      </span>
                    </>
                  ) : (
                    <span className="ownership-status unknown">Unknown</span>
                  )}
                </div>
              </div>
              <div className="player-details">
                <span className="position">{selectedPlayer.position}</span>
                <span className="age">{selectedPlayer.age} years</span>
                <span className="value">{numToPrice(selectedPlayer.value)}</span>
              </div>
            </div>
          )}

          {submitError && <div className="error-message">{submitError}</div>}

          <div className="bid-form">
            <div className="form-group">
              <label htmlFor="bidAmount">Transfer Fee (€)</label>
              <input
                type="text"
                inputMode="numeric"
                id="bidAmount"
                value={bidAmount === "" ? "" : Number(bidAmount).toLocaleString("en-US")}
                readOnly={isQuickplayLeague}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "");
                  setBidAmount(digits);
                  const numericVal = parseInt(digits, 10);
                  if (!Number.isNaN(numericVal) && numericVal >= minBid) {
                    setBidError(null);
                  } else {
                    setBidError(`Minimum allowed ${numToPrice(minBid)}`);
                  }
                }}
              />
              {hasHighestBid && (
                <small className="helper-text">Current highest bid {numToPrice(currentPrice)}</small>
              )}
              {isQuickplayLeague ? (
                <small className="helper-text">
                  {quickplayPendingBid
                    ? "You already have a pending bid for this player."
                    : "Quick Play bids are fixed at the player's transfer value."}
                </small>
              ) : (
                <small className="helper-text">Minimum allowed {numToPrice(minBid)}</small>
              )}
              {bidError && <div className="error-message">{bidError}</div>}
              {insufficientBudget && (
                <div className="error-message">
                  You don't have enough remaining budget for this bid.
                </div>
              )}
            </div>
          </div>

          <div className="buttons-block">
            <button
              type="button"
              className="submit-btn trans-btn white"
              onClick={closePopup}
            >
              CANCEL
            </button>
            <button
              type="button"
              className="submit-btn submit_offer_btn"
              onClick={async () => {
                if (selectedPlayer) {
                  const bidData = {
                    playerId: selectedPlayer.wyId,
                    amount: isQuickplayLeague ? quickplayFixedBid : parseInt(bidAmount),
                  };
                  if (bidData.amount < minBid) {
                    setBidError(`Bid must be at least ${numToPrice(minBid)}`);
                    return;
                  }
                  const loadingToastId = toast.loading('Submitting bid...');
                  try {
                    await handleSubmit(bidData);
                    posthog.capture('transfer_bid_submitted', {
                      player_id: selectedPlayer.wyId,
                      player_name: selectedPlayer.name,
                      player_position: selectedPlayer.position,
                      bid_amount: bidData.amount,
                      is_free_agent: isFreeAgent,
                    });
                    toast.update(loadingToastId, {
                      render: `Bid of €${(bidData.amount / 1000000).toFixed(1)}M submitted successfully!`,
                      type: 'success',
                      isLoading: false,
                      autoClose: 3000
                    });
                    if (selectedPlayer?.wyId) {
                      queryClient.invalidateQueries({ queryKey: ["player-market-data", selectedPlayer.wyId] });
                    }
                    closePopup();
                  } catch (error) {
                    posthog.captureException(error, { event: 'transfer_bid_failed', player_id: selectedPlayer?.wyId });
                    const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
                    toast.update(loadingToastId, {
                      render: `Failed to submit bid: ${errorMessage}`,
                      type: 'error',
                      isLoading: false,
                      autoClose: 5000
                    });
                  }
                }
              }}
              disabled={submitting || quickplayPendingBid || Number(bidAmount) < minBid || insufficientBudget}
            >
              {submitting ? "SUBMITTING..." : "SUBMIT BID"}
            </button>
          </div>
        </Popup>
      );
      break;

    case "submit":
      const minimumRequiredBudget = 0;
      if (info.remainingBudget < minimumRequiredBudget) {
        popupContent = (
          <Popup popOpen={popOpen} close={closePopup}>
            <h3>INSUFFICIENT FUNDS</h3>
            <div className="offer-description text-center">
              You don't have enough remaining budget to complete this operation.
            </div>
            <div className="buttons-block">
              <button
                type="button"
                className="submit-btn trans-btn white"
                onClick={closePopup}
              >
                CLOSE
              </button>
              {!info.boosted && (
                <button
                  type="button"
                  className="submit-btn"
                  onClick={() => setPopEvent("boost")}
                >
                  BOOST
                </button>
              )}
            </div>
          </Popup>
        );
        break;
      }
      if (info.squadSize < 22) {
        popupContent = (
          <Popup popOpen={popOpen} close={closePopup}>
            <h3>NOT ENOUGH PLAYERS</h3>
            <div className="offer-description text-center">
              Your squad must have at least 22 players.
            </div>
            <div className="buttons-block">
              <button
                type="button"
                className="submit-btn trans-btn white"
                onClick={closePopup}
              >
                CLOSE
              </button>
            </div>
          </Popup>
        );
        break;
      }
      popupContent = (
        <Popup popOpen={popOpen} close={closePopup}>
          <h3>FINISH TRANSFERS</h3>
          <div className="offer-description text-center">
            Are you sure that you have completed all of your transfers? You will
            not be able to buy or sell players until the next transfer window.
          </div>
          <div className="buttons-block">
            <button
              type="button"
              className="submit-btn trans-btn white"
              onClick={closePopup}
            >
              CANCEL
            </button>
            <button
              type="button"
              className="submit-btn submit_offer_btn"
              onClick={handleSubmit}
            >
              FINISH
            </button>
          </div>
        </Popup>
      );
      break;

    case "drop":
      popupContent = (
        <Popup popOpen={popOpen} close={closePopup}>
          <h3>CONFIRM DROP PLAYER</h3>
          {selectedPlayer && (
            <div className="player-info" style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div className="player-name" style={{ fontSize: '1.2em', fontWeight: 'bold' }}>{selectedPlayer.name}</div>
            </div>
          )}
          <div className="offer-description text-center" style={{ marginBottom: '20px' }}>
            Are you sure you want to drop {selectedPlayer?.name || 'this player'}?
            This will remove them from your squad and refund their last transfer fee to your budget.
            This action cannot be undone.
          </div>
          {submitError && <div className="error-message" role="alert">{submitError}</div>}
          <div className="buttons-block">
            <button
              type="button"
              className="submit-btn trans-btn white"
              onClick={closePopup}
              disabled={submitting}
            >
              CANCEL
            </button>
            <button
              type="button"
              className="submit-btn" 
              onClick={handleDrop}
              disabled={submitting}
            >
              {submitting ? "DROPPING..." : "CONFIRM DROP"}
            </button>
          </div>
        </Popup>
      );
      break;

    case "boost":
      popupContent = (
        <Popup popOpen={popOpen} close={closePopup}>
          <h3>BOOST TRANSFER BUDGET</h3>
          <div className="boost-options">{BoostOptions}</div>
          <div className="buttons-block">
            <button
              type="button"
              className="submit-btn trans-btn white"
              onClick={closePopup}
            >
              CANCEL
            </button>
            <button
              type="button"
              className="submit-btn submit_offer_btn"
              onClick={() => {
                handleBoost(selectedOption);
              }}
              disabled={submitting || !selectedOption}
            >
              {submitting ? "BOOSTING..." : "CONFIRM BOOST"}
            </button>
          </div>
        </Popup>
      );
      break;

    default:
      popupContent = (
        <Popup popOpen={popOpen} close={closePopup}>
          <h3>CONFIRM ACTION</h3>
          <p>Are you sure you want to proceed?</p>
          <div className="buttons-block">
            <button
              type="button"
              className="submit-btn trans-btn white"
              onClick={closePopup}
            >
              CANCEL
            </button>
            <button
              type="button"
              className="submit-btn"
              onClick={handleSubmit} 
            >
              CONFIRM
            </button>
          </div>
        </Popup>
      );
  }

  return popupContent;
};

export { TransferFlowPopup };
